import { useEffect, useState } from 'react';
import { Link, useRouter, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Loader2,
  MailCheck,
  RefreshCcw,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useToast } from '@/shared/ui/toast-provider';
import {
  postAuthResendVerification,
  postAuthVerifyEmail,
} from '@/entities/auth';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { cn } from '@/shared/utils/cn';
import { normalizeEmail } from '@/shared/utils/normalize-email';

const backgroundImage =
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop';

export const VerifyEmailPage = () => {
  const router = useRouter();
  const toast = useToast();
  const { email, token } = useSearch({ from: '/(auth)/verify-email' });

  const [resendError, setResendError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState(email ?? '');
  const {
    mutateAsync: verifyEmail,
    status: verifyStatus,
    isPending: isVerifyPending,
    isSuccess: isVerifySuccess,
    isError: isVerifyError,
  } = useMutation({
    mutationFn: postAuthVerifyEmail,
    onSuccess: () => {
      setVerifyError(null);
      toast({
        type: 'success',
        title: 'Email verified',
        description: 'You can now sign in with your credentials.',
      });
    },
    onError: (mutationError) => {
      const message = resolveErrorMessage(
        mutationError,
        'We could not verify your email. Please try again.',
      );
      // Surfaced inline only (verify error → card description) — no error toast.
      setVerifyError(message);
    },
  });

  const { mutateAsync: resendVerification, isPending: isResendPending } =
    useMutation({
      mutationFn: postAuthResendVerification,
      onSuccess: (data) => {
        setResendError(null);
        const description =
          (data && typeof data === 'object' && 'message' in data
            ? (data as { message?: string }).message
            : null) ?? 'We have sent you a new verification link.';
        toast({
          type: 'success',
          title: 'Verification email sent',
          description,
        });
      },
      onError: (mutationError) => {
        const message = resolveErrorMessage(
          mutationError,
          'We could not resend the verification email. Please try again.',
        );
        // Surfaced inline only (resend error below the email input) — no error toast.
        setResendError(message);
      },
    });

  useEffect(() => {
    if (!token) return;
    verifyEmail({
      token,
    }).catch(() => {
      // handled by onError
    });
  }, [token, verifyEmail]);

  const iconVariant = isVerifySuccess
    ? 'bg-emerald-500/10 text-emerald-200 border border-emerald-400/30'
    : isVerifyError
      ? 'bg-red-500/10 text-red-200 border border-red-400/30'
      : 'bg-blue-500/10 text-blue-200 border border-blue-400/30';

  const icon = isVerifySuccess ? (
    <Check className="h-7 w-7" />
  ) : isVerifyError ? (
    <AlertTriangle className="h-7 w-7" />
  ) : (
    <MailCheck className="h-7 w-7" />
  );

  const title = isVerifySuccess
    ? 'Email Verified'
    : isVerifyPending
      ? 'Verifying Email...'
      : isVerifyError
        ? 'Verification Required'
        : token
          ? 'Verifying Email'
          : 'Check Your Email';

  const description = isVerifySuccess
    ? 'Great! Your email is confirmed. You can proceed to sign in to your account.'
    : isVerifyPending
      ? 'Hold tight while we confirm your email. This won’t take long.'
      : isVerifyError && verifyError
        ? verifyError
        : token
          ? 'We are validating your verification link. Please wait a moment.'
          : email
            ? `We sent a verification link to ${email}. Click the link in your inbox to activate your account.`
            : 'Enter your email address below to resend the verification link.';

  const handleResend = async () => {
    if (!emailInput.trim()) {
      setResendError('Please enter a valid email address.');
      return;
    }
    setResendError(null);
    try {
      await resendVerification({ email: normalizeEmail(emailInput) });
    } catch {
      // handled in onError
    }
  };

  const handleGoToSignIn = () => {
    router.navigate({
      to: '/sign-in',
      search: { redirect: undefined },
    });
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 pb-12 pt-28 text-slate-50">
      <img
        src={backgroundImage}
        alt="Email verification background"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/70" aria-hidden />

      <Link
        to="/sign-in"
        search={{ redirect: undefined }}
        className="group absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/60 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-slate-950/40 backdrop-blur transition hover:bg-slate-900/80 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition group-hover:-translate-x-0.5" />
        Back
      </Link>

      <section className="relative z-10 w-full max-w-[480px] space-y-8 rounded-[32px] border border-white/10 bg-slate-900/80 p-10 text-slate-50 shadow-[0_30px_80px_rgba(10,17,33,0.55)] backdrop-blur-2xl">
        <header className="space-y-4 text-center">
          <div
            className={cn(
              'mx-auto flex h-16 w-16 items-center justify-center rounded-full',
              iconVariant,
            )}
          >
            {isVerifyPending ? (
              <Loader2 className="h-7 w-7 animate-spin" />
            ) : (
              icon
            )}
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              {title}
            </h1>
            <p className="text-sm leading-relaxed text-slate-200/80">
              {description}
            </p>
          </div>
        </header>

        {!token && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-xs font-semibold uppercase tracking-wide text-slate-300/70"
              >
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                placeholder="you@example.com"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                className="h-12 rounded-2xl border-white/20 bg-white/10 text-base text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>

            {resendError && (
              <p className="text-sm font-semibold text-amber-300">
                {resendError}
              </p>
            )}

            <Button
              type="button"
              variant="glow"
              size="lg"
              className="w-full gap-2 text-base"
              onClick={handleResend}
              disabled={isResendPending}
            >
              {isResendPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending email...
                </>
              ) : (
                <>
                  <RefreshCcw className="h-4 w-4" />
                  Resend Verification Email
                </>
              )}
            </Button>
          </div>
        )}

        <div className="space-y-3">
          <Button
            type="button"
            variant="glass"
            size="lg"
            className="w-full gap-2 text-base"
            onClick={handleGoToSignIn}
          >
            <MailCheck className="h-4 w-4" />
            Go to Sign In
          </Button>
        </div>
      </section>
    </main>
  );
};
